// client/src/services/analyticsCollector.ts
// Collect and send analytics data to backend

export interface AnalyticsEvent {
  type: string;
  timestamp: number;
  data: Record<string, any>;
  userAgent: string;
  url: string;
  geography?: string;
  deviceType?: string;
}

class AnalyticsCollector {
  private queue: AnalyticsEvent[] = [];
  private batchSize = 10;
  private flushIntervalMs = 30000; // 30 seconds
  private flushTimer: NodeJS.Timeout | null = null;
  private initialized = false;

  constructor() {
    this.startFlushTimer();
    // Flush on page unload
    window.addEventListener('beforeunload', () => this.flush());
  }

  initialize() {
    if (this.initialized) return;
    this.initialized = true;  }

  trackChunkLoad(chunkName: string, success: boolean, duration: number) {
    this.track('chunk_load', {
      chunk: chunkName,
      success,
      duration,
      size: this.estimateChunkSize(chunkName),
    });
  }

  trackChunkError(chunkName: string, error: Error, retryCount: number) {
    this.track('chunk_error', {
      chunk: chunkName,
      error: error.message,
      stack: error.stack,
      retryCount,
    });
  }

  trackRecoveryAttempt(source: string, success: boolean, duration: number) {
    this.track('recovery_attempt', {
      source,
      success,
      duration,
    });
  }

  trackDeploymentEvent(version: string, event: string, data: Record<string, any>) {
    this.track('deployment_event', {
      version,
      event,
      ...data,
    });
  }

  trackPerformanceMetric(metric: string, value: number) {
    this.track('performance_metric', {
      metric,
      value,
    });
  }

  trackUserAction(action: string, data: Record<string, any> = {}) {
    this.track('user_action', {
      action,
      ...data,
    });
  }

  private track(type: string, data: Record<string, any>) {
    const event: AnalyticsEvent = {
      type,
      timestamp: Date.now(),
      data,
      userAgent: navigator.userAgent,
      url: window.location.href,
      geography: this.getGeography(),
      deviceType: this.getDeviceType(),
    };

    this.queue.push(event);

    // Flush if batch is full
    if (this.queue.length >= this.batchSize) {
      this.flush();
    }
  }

  private async flush() {
    if (this.queue.length === 0) return;

    const events = this.queue.splice(0, this.batchSize);

    try {
      // Use sendBeacon for reliability (fires even if page is unloading)
      const payload = JSON.stringify({ events });
      navigator.sendBeacon('/api/analytics/events', payload);
    } catch (error) {
      // Re-queue events if failed
      this.queue.unshift(...events);
    }
  }

  private startFlushTimer() {
    this.flushTimer = setInterval(() => this.flush(), this.flushIntervalMs);
  }

  private getGeography(): string {
    // Try to get from localStorage or browser API
    try {
      return (navigator as any).language || 'unknown';
    } catch {
      return 'unknown';
    }
  }

  private getDeviceType(): string {
    const ua = navigator.userAgent.toLowerCase();
    if (/mobile|android|iphone|ipad/.test(ua)) return 'mobile';
    if (/tablet|ipad/.test(ua)) return 'tablet';
    return 'desktop';
  }

  private estimateChunkSize(chunkName: string): number {
    // Try to estimate from performance API
    try {
      const resources = performance.getEntriesByName(chunkName);
      if (resources.length > 0) {
        const resource = resources[0] as PerformanceResourceTiming;
        return resource.transferSize || 0;
      }
    } catch {}
    return 0;
  }

  destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flush();
  }
}

export const analyticsCollector = new AnalyticsCollector();

